'use client';

import { useRequest } from 'ahooks';
import { getRentsLessee, getRentsLessor } from '@/lib/api/methods';
import {
  createContext,
  Dispatch,
  JSX,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { AccountContext } from './accountContext';
import { LanguageContext } from './languageContext';
import { NotificationContext } from './notificationContext';
import { DebtRentType } from '@/lib/types';

export type RentStatus =
  | 'PENDING_APPROVAL'
  | 'ACCEPTED'
  | 'BOOKED'
  | 'CANCELLED'
  | 'WAITING_FOR_LESSEE_ACCEPT'
  | 'STARTED'
  | 'WAITING_LESSEE_PICK_UP'
  | 'WAITING_COURIER_ACCEPTING'
  | 'DELIVERY_IN_PROGRESS '
  | 'WAITING_COURIER_ACCEPTING'
  | 'RETURN_IN_PROGRESS'
  | 'DEBATE'
  | 'WAITING_FOR_LESSOR_ACCEPT'
  | 'WAITING_FOR_RETURN'
  | 'PRODUCT_RETURNED'
  | 'FINISH_PAYMENT_PROCESS'
  | 'FAIL_FINISH_PAYMENT_PROCESS'
  | 'COMPLETED';

export type LessData = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  patronymic?: string;
  avatar?: string;
  description: string;
  cityId: number;
  rating?: number;
  online: boolean;
  lastOnline: string;
  reviewCount?: number;
};

export type RenterProduct = {
  id: string;
  name: string;
  productGroup: {
    id: number;
    name: number;
    description: number;
    languageCode: string;
  };
  userId: string;
  status: string;
  video: string;
  description: string;
  photos: [
    {
      original: string;
      preview: string;
      path: string;
    },
  ];
  price: number;
  rentalDay: number;
  rentalMonth: number;
  rentalWeek: number;
  unavailableWeekdays: {
    MONDAY: 'Monday';
    TUESDAY: 'Tuesday';
    WEDNESDAY: 'Wednesday';
    THURSDAY: 'Thursday';
    FRIDAY: 'Friday';
    SATURDAY: 'Saturday';
    SUNDAY: 'Sunday';
  };
  unavailableDates: string[];
  productAddress: {
    address: string;
    longitude: number;
    latitude: number;
  };
  productGroupId: string;
  productOption: [
    {
      optionId: string;
      value: string;
      selectValues: number[];
      option: {
        languageCode: string;
        name: string;
        type: string;
      };
    },
  ];
  activeComment: string; // start
  createdAt: string;
  originalPhotos: string[];
  // previewPhotos: string[];
  rating?: any;
  reviewCount: number;
  updatedAt: string;
};

export type Rent = {
  lessorData: LessData;
  lesseeData: LessData;
  debates: any[];
  debt: DebtRentType;
  totalCardPrice: string;
  totalTokenPrice: string;
  id: string;
  status: RentStatus;
  insuranceType: string;
  totalPrice: string;
  serviceFee: number;
  dateStart: string;
  taxValue: number;
  taxPercent: number;
  dateEnd: string;
  createdAt: string;
  updatedAt: string;
  lessorId: string;
  lesseeId: string;
  productId: string;
  rentalPlaceOfUseId: number;
  product: RenterProduct;
  delivery: boolean;
  deliveryPrice: string;
  frozenFundsId?: string;
  insuranceDeposit: string;
  insurancePercentValue: string;
  insuranceValue: string;
  rentPrice: string;
  chatId: string;
  rentPriceNet: string;
  transferStartDate: string;
  returnTransferDate: string;
};

export type Rents = {
  counts: number;
  rents: Rent[];
};

export type Pagination = { take: number; skip: number; total: number };

export type ContextProps = {
  requests: Rents;
  requestsLoading: boolean;
  onPaginationChange: (page: number) => void;
  pagination: Pagination;
  isCreatePartOpen: boolean;

  setIsCreatePartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshRequests: Dispatch<SetStateAction<boolean>>;
  setRentStatuses: Dispatch<
    SetStateAction<{ statuses: string; isDispute: boolean }>
  >;
};

export const REQUESTS_PER_PAGE = 10;
export const RenterContext = createContext({} as ContextProps);

export const RenterProvider = ({ children }: { children: JSX.Element }) => {
  const { isAuthorized, isRenter, accountData } = useContext(AccountContext);
  const { countryCode, language } = useContext(LanguageContext);
  const [pagination, setPagination] = useState<Pagination>({
    take: REQUESTS_PER_PAGE,
    skip: 0,
    total: 0,
  });
  const [refreshRequests, setRefreshRequests] = useState(false);
  const [isCreatePartOpen, setIsCreatePartOpen] = useState(false);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [rentStatuses, setRentStatuses] = useState<{
    statuses: string;
    isDispute: boolean;
  }>();
  const { loading: requestsLoading, data: requests } = useRequest(
    () =>
      isRenter
        ? getRentsLessee(
            pagination,
            rentStatuses.statuses,
            !!rentStatuses.isDispute,
            accountData?.country
          )
        : getRentsLessor(
            pagination,
            rentStatuses.statuses,
            !!rentStatuses.isDispute,
            accountData?.country
          ),
    {
      ready: !!countryCode && isAuthorized && !!rentStatuses && !!accountData,
      refreshDeps: [pagination, refreshRequests, language, rentStatuses],
      onError: (error: any) => {
        if (error?.response.data.message) {
          openNotificationWithIcon('error', error.response.data.message);
        } else {
          openNotificationWithIcon('error', error.message);
        }
      },
    }
  );

  const onPaginationChange = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      skip: page * REQUESTS_PER_PAGE - REQUESTS_PER_PAGE,
    }));
  }, []);

  const value = useMemo(
    () => ({
      requests,
      requestsLoading,
      pagination,
      onPaginationChange,
      setRefreshRequests,
      setRentStatuses,
      setIsCreatePartOpen,
      isCreatePartOpen,
    }),
    [
      requests,
      requestsLoading,
      pagination,
      onPaginationChange,
      isCreatePartOpen,
    ]
  );

  return (
    <RenterContext.Provider value={value}>{children}</RenterContext.Provider>
  );
};
